import React, { forwardRef, useState, useLayoutEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { Box, Typography } from "@mui/material";

const ROW_HEIGHT = 52;
const PADDING = 8;
const OVERSCAN = 5;

// MUI supplies option data rather than creating thousands of DOM elements.
// The scroll container keeps the full height and keyboard navigation of the list.
export function prepareCustomerNavigation(event) {
  const diff = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 }[event.key];
  if (!diff || event.which === 229) return;
  const input = event.currentTarget.querySelector("input");
  const listbox = document.getElementById(input?.getAttribute("aria-controls"));
  if (!listbox?.showOption) return;
  const activeIndex = input.getAttribute("aria-activedescendant")?.match(/-option-(\d+)$/);
  const current = activeIndex ? Number(activeIndex[1]) : -1;
  const count = Number(listbox.dataset.optionCount);
  let next = current + diff;
  if (next < 0) next = Math.abs(diff) > 1 ? 0 : count - 1;
  if (next >= count) next = Math.abs(diff) > 1 ? count - 1 : 0;
  // Mount the next row before MUI checks whether it can receive keyboard focus.
  listbox.showOption(next);
}

const CustomerListbox = forwardRef(function CustomerListbox({ children, style, filterKey, ...props }, ref) {
  const [scrollTop, setScrollTop] = useState(0);
  const nodeRef = useRef(null);
  const options = children || [];
  useLayoutEffect(() => {
    if (nodeRef.current) nodeRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [filterKey]);
  const setNode = useCallback((node) => {
    nodeRef.current = node;
    if (node) node.showOption = (index) => {
      const top = PADDING + index * ROW_HEIGHT;
      if (top < node.scrollTop || top + ROW_HEIGHT > node.scrollTop + node.clientHeight) {
        node.scrollTop = Math.max(0, top - ROW_HEIGHT);
        flushSync(() => setScrollTop(node.scrollTop));
      }
    };
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);
  const viewportHeight = Math.min(options.length * ROW_HEIGHT + PADDING * 2, 320);
  const start = Math.max(0, Math.floor((scrollTop - PADDING) / ROW_HEIGHT) - OVERSCAN);
  const end = Math.min(options.length, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN);

  return (
    <ul
      {...props}
      data-option-count={options.length}
      ref={setNode}
      style={{ ...style, position: "relative", height: viewportHeight, maxHeight: "40vh", overflow: "auto" }}
      onScroll={(event) => {
        setScrollTop(event.currentTarget.scrollTop);
        props.onScroll?.(event);
      }}
    >
      <li role="presentation" aria-hidden="true" style={{ height: options.length * ROW_HEIGHT, listStyle: "none", pointerEvents: "none" }} />
      {options.slice(start, end).map(([optionProps, option, isDark], offset) => {
        const { key, ...liProps } = optionProps;
        return (
          <li
            {...liProps}
            key={key}
            style={{ ...liProps.style, position: "absolute", top: PADDING + (start + offset) * ROW_HEIGHT, height: ROW_HEIGHT, width: "100%", boxSizing: "border-box" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ color: isDark ? "#f1f5f9" : "#111827" }}>
                {option.WorkdayCustomerBusinessEntityName}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
                {[option.CompanyReferenceID, option.SourceSystemCode].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          </li>
        );
      })}
    </ul>
  );
});

export default CustomerListbox;
